/** Google GenerateContent policy. No environment, provider SDK or app imports. */
export const DEFAULT_GEMINI_MODEL = "gemini-3.8-flash" as const;
export const GEMINI_INPUT_TOKEN_LIMIT = 1_048_576;
export const GEMINI_OUTPUT_TOKEN_LIMIT = 65_536;
export const GEMINI_THINKING_HEADROOM = 8_192;
export type GeminiThinkingLevel = "low" | "medium" | "high";

/** Retire only general-purpose Flash pins; preserve embeddings and Live models. */
export function resolveActiveGeminiModel(model: string): string {
  return model === "gemini-3.5-flash" ||
    model === "gemini-3.6-flash" ||
    model === "gemini-3.7-flash"
    ? DEFAULT_GEMINI_MODEL
    : model;
}

/** Total output includes thinking. Reject invalid budgets before provider I/O. */
export function geminiOutputBudget(totalTokens: number): number {
  if (!Number.isSafeInteger(totalTokens) || totalTokens <= 0) {
    throw new RangeError("Gemini output budget must be a positive integer");
  }
  return Math.min(totalTokens, GEMINI_OUTPUT_TOKEN_LIMIT);
}

/** Existing chat callers specify visible output; reserve bounded thinking room. */
export function geminiChatOutputBudget(visibleTokens = 2_048): number {
  return geminiOutputBudget(
    geminiOutputBudget(visibleTokens) + GEMINI_THINKING_HEADROOM,
  );
}

/** Global standard rates per million tokens, including thinking at output rate.
 * https://ai.google.dev/gemini-api/docs/pricing (verified 2026-09-06).
 * Historical usage retains its stored amount.
 */
export function geminiFlashPricingAt(at: Date = new Date()) {
  if (!Number.isFinite(at.getTime())) {
    throw new RangeError("Invalid usage date");
  }
  const introductory = at.getTime() < Date.UTC(2027, 0, 1);
  return {
    inputPerMillion: introductory ? 0.75 : 1.5,
    outputPerMillion: introductory ? 3.75 : 7.5,
    cachedInputPerMillion: introductory ? 0.075 : 0.15,
  };
}
